import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Shield, Smartphone, History, AlertTriangle, CheckCircle,
  XCircle, Loader2, Lock, Eye, Monitor
} from 'lucide-react';
import TwoFactorSetup from './TwoFactorSetup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SecurityDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [twoFactorStatus, setTwoFactorStatus] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadSecurityData();
  }, []);

  const loadSecurityData = async () => {
    try {
      setLoading(true);
      const [statusRes, historyRes, eventsRes, sessionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/security/2fa/status`),
        axios.get(`${API_URL}/api/security/audit/login-history?limit=10`),
        axios.get(`${API_URL}/api/security/audit/security-events?limit=10`),
        axios.get(`${API_URL}/api/security/sessions`)
      ]);

      setTwoFactorStatus(statusRes.data);
      setLoginHistory(historyRes.data);
      setSecurityEvents(eventsRes.data);
      setActiveSessions(sessionsRes.data);
    } catch (error) {
      console.error('Error loading security data:', error);
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async () => {
    const code = prompt('Enter your 2FA code to disable two-factor authentication:');
    if (!code) return;

    try {
      setLoading(true);
      await axios.post(`${API_URL}/api/security/2fa/disable`, { code });
      setMessage({ type: 'success', text: '2FA disabled successfully' });
      await loadSecurityData();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to disable 2FA'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {message && (
        <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
          {message.type === 'success' ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      {/* 2FA Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && !twoFactorStatus ? (
            <div className="text-center py-4">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-teal-600" />
            </div>
          ) : showTwoFactorSetup ? (
            <TwoFactorSetup
              onComplete={() => {
                setShowTwoFactorSetup(false);
                loadSecurityData();
                setMessage({ type: 'success', text: '2FA enabled successfully!' });
              }}
            />
          ) : twoFactorStatus?.enabled ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div className="flex-1">
                  <p className="font-semibold text-green-900">2FA is Active</p>
                  <p className="text-sm text-green-700">
                    Your account is protected with two-factor authentication
                  </p>
                  {twoFactorStatus.enabled_at && (
                    <p className="text-xs text-green-600 mt-1">
                      Enabled: {new Date(twoFactorStatus.enabled_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Backup Codes</p>
                  <p className="text-xs text-gray-600">
                    {twoFactorStatus.backup_codes_remaining} codes remaining
                  </p>
                </div>
                {twoFactorStatus.backup_codes_remaining < 3 && (
                  <Badge variant="outline" className="text-orange-600 border-orange-600">
                    Low
                  </Badge>
                )}
              </div>

              <Button
                variant="outline"
                onClick={disable2FA}
                disabled={loading}
                className="w-full text-red-600 hover:text-red-700 border-red-200"
              >
                Disable 2FA
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
                <div>
                  <p className="font-semibold text-yellow-900">2FA is Not Enabled</p>
                  <p className="text-sm text-yellow-700">
                    Your account is less secure without 2FA
                  </p>
                </div>
              </div>

              <Button
                onClick={() => setShowTwoFactorSetup(true)}
                className="w-full bg-teal-600 hover:bg-teal-700"
              >
                <Shield className="mr-2 h-4 w-4" />
                Enable Two-Factor Authentication
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <History className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{loginHistory.length}</p>
                <p className="text-sm text-gray-600">Recent Logins</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Monitor className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{activeSessions.length}</p>
                <p className="text-sm text-gray-600">Active Sessions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Lock className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{securityEvents.length}</p>
                <p className="text-sm text-gray-600">Security Events</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderLoginHistory = () => (
    <Card>
      <CardHeader>
        <CardTitle>Login History</CardTitle>
        <CardDescription>Recent login attempts to your account</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-teal-600" />
          </div>
        ) : loginHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <History className="h-12 w-12 mx-auto mb-2 opacity-20" />
            <p>No login history available</p>
          </div>
        ) : (
          <div className="space-y-2">
            {loginHistory.map((login) => (
              <div
                key={login.id}
                className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50"
              >
                <div className="mt-1">
                  {login.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {login.success ? 'Successful Login' : 'Failed Login'}
                    </span>
                    <Badge variant={login.success ? 'default' : 'destructive'} className="text-xs">
                      {login.success ? 'Success' : 'Failed'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">
                    IP: {login.ip_address}
                  </p>
                  {login.failure_reason && (
                    <p className="text-xs text-red-600">Reason: {login.failure_reason}</p>
                  )}
                </div>
                <div className="text-right text-sm text-gray-500">
                  <p>{new Date(login.timestamp).toLocaleDateString()}</p>
                  <p className="text-xs">{new Date(login.timestamp).toLocaleTimeString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderSecurityEvents = () => (
    <Card>
      <CardHeader>
        <CardTitle>Security Events</CardTitle>
        <CardDescription>Recent security-related activities</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-teal-600" />
          </div>
        ) : securityEvents.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Shield className="h-12 w-12 mx-auto mb-2 opacity-20" />
            <p>No security events</p>
          </div>
        ) : (
          <div className="space-y-2">
            {securityEvents.map((event) => (
              <div
                key={event.id}
                className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50"
              >
                <div className="mt-1">
                  {event.severity === 'critical' && <XCircle className="h-5 w-5 text-red-600" />}
                  {event.severity === 'warning' && <AlertTriangle className="h-5 w-5 text-yellow-600" />}
                  {event.severity === 'info' && <CheckCircle className="h-5 w-5 text-blue-600" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{event.event_type.replace(/_/g, ' ').toUpperCase()}</span>
                    <Badge
                      variant={
                        event.severity === 'critical' ? 'destructive' :
                        event.severity === 'warning' ? 'outline' :
                        'default'
                      }
                      className="text-xs"
                    >
                      {event.severity}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">{event.description}</p>
                  <p className="text-xs text-gray-500 mt-1">IP: {event.ip_address}</p>
                </div>
                <div className="text-right text-sm text-gray-500">
                  <p>{new Date(event.timestamp).toLocaleDateString()}</p>
                  <p className="text-xs">{new Date(event.timestamp).toLocaleTimeString()}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Security Settings</h1>
        <p className="text-gray-600">Manage your account security and authentication</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 font-medium transition ${
            activeTab === 'overview'
              ? 'text-teal-600 border-b-2 border-teal-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Shield className="inline h-4 w-4 mr-2" />
          Overview
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-medium transition ${
            activeTab === 'history'
              ? 'text-teal-600 border-b-2 border-teal-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <History className="inline h-4 w-4 mr-2" />
          Login History
        </button>
        <button
          onClick={() => setActiveTab('events')}
          className={`px-4 py-2 font-medium transition ${
            activeTab === 'events'
              ? 'text-teal-600 border-b-2 border-teal-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Eye className="inline h-4 w-4 mr-2" />
          Security Events
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'history' && renderLoginHistory()}
      {activeTab === 'events' && renderSecurityEvents()}
    </div>
  );
}
